import React, {CSSProperties, useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  GIFTSHOP_TRANSACTION_DETAIL_DESKTOP_WIDTH,
  GIFTSHOP_TRANSACTION_DETAIL_TABLET_WIDTH,
  WINDOW_MODE_MOBILE_WIDTH,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {useWindowMode} from 'windows'
import {
  getCurrencyValue,
  parseDate,
  showSnackbar,
  useHistory,
  useTransactionHowToPay,
  useTransactionPaymentProofHooks,
} from 'utils'
import {
  GiftShopContentData,
  GiftShopInvoiceDetail,
  TransactionStatus,
} from 'types'
import {Button, Icon, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {
  GiftShopTemplateDetailContentMultipleListModal,
  GiftShopTemplatePaymentProofModal,
} from 'pages'
import {TransactionWaitingDetailContentProps} from './TransactionDetailContentProps'
import {
  TransactionDetailContentItemListSingleCreator,
  TransactionDetailContentItemListMultiCreator,
} from '../ContentItem'
import {TransactionDetailContentHeader} from '../ContentHeader'

const StyledContainer = styled.div<CSSProperties>`
  display: flex;
  width: ${convertUnit(GIFTSHOP_TRANSACTION_DETAIL_DESKTOP_WIDTH)};
  margin: ${convertUnit(25)};
  padding: ${convertUnit(25)} 0;
  border-radius: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  align-self: center;
  overflow: auto;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: ${convertUnit(GIFTSHOP_TRANSACTION_DETAIL_TABLET_WIDTH)};
    flex-direction: column;
    height: 100%;
    padding: ${convertUnit(25)};
  }
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    width: calc(100% - ${convertUnit(50)});
    min-height: 100%;
    margin: 0;
  }
`

const StyledContentSection = styled.div<CSSProperties>`
  display: flex;
  width: ${({width}) => width};
  flex-direction: column;
  margin: 0 ${convertUnit(25)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: 100%;
    margin: 0;
    margin-bottom: ${convertUnit(16)};
  }
`

const StyledInfoContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-right: ${convertUnit(25)};
`

const StyledInfoItem = styled.div`
  width: 50%;
  margin-bottom: ${convertUnit(16)};
`

const StyledMessageContainer = styled.div`
  ${({theme}) => ({
    backgroundColor: theme.white_4,
  })}
  flex-direction: 'column';
  border-radius: ${convertUnit(8)};
  width: 100%;
  display: flex;
  box-sizing: border-box;
  padding: ${convertUnit(8)} ${convertUnit(16)};
  margin-top: ${convertUnit(4)};
  margin-bottom: ${convertUnit(20)};
`

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  margin-bottom: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  display: flex;
  flex: 1;
  width: 100%;
`

const StyledStrikedText = styled(Paragraph)`
  text-decoration: line-through;
`

const StyledDate = styled(Paragraph)`
  margin-bottom: ${convertUnit(4)};
`

const StyledBadge = styled.div`
  width: fit-content;
  background-color: ${({theme}) => theme.primary_1};
  padding: ${convertUnit(2)} ${convertUnit(8)};
  border-radius: ${convertUnit(6)};
  margin-bottom: ${convertUnit(8)};
`

const StyledInvoiceIdContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(4)};
`

const StyledCopyIcon = styled(Icon)`
  width: ${convertUnit(8)};
  height: ${convertUnit(8)};
  margin-right: ${convertUnit(8)};
  cursor: pointer;
`

export default function TransactionWaitingDetailContent({
  stateData,
  dataInvoice,
}: TransactionWaitingDetailContentProps) {
  const {translate} = useTranslation()
  const mode = useWindowMode()
  const {
    data,
    payment_invoice,
    payment_type,
    transaction_method,
    transaction_total,
    transaction_date,
    price_detail,
  } = stateData
  const creators = useMemo(() => data.map((entry) => entry.creator_name), [
    data,
  ])
  const invoiceIDs = useMemo(
    () => data.map((val) => val.invoice_id).toString(),
    [data],
  )

  const {
    stateSendProofModal,
    stateSendProofLoading,
    stateSendProofSent,
    handleSubmitPaymentProof,
  } = useTransactionPaymentProofHooks({
    invoice_id: invoiceIDs,
    payment_invoice,
  })

  const [sendProofModal, setSendProofModal] = stateSendProofModal
  const sendProofLoading = stateSendProofLoading[0]
  const sendProofSent = stateSendProofSent[0]
  const [highlightedIndex, setHighlightedIndex] = useState<number>()

  const {enableHowToPayButton, handleHowToPay} = useTransactionHowToPay({
    payment_invoice,
    payment_type,
    transaction_method,
    transaction_date,
    transaction_total,
  })
  const history = useHistory()

  const handleClickPendingItem = useCallback(
    (item: GiftShopContentData, thisStatus: TransactionStatus) => {
      history.push('giftshop_transaction_pending_content_detail', {
        data: [item],
        transaction_date,
        statusNow: thisStatus,
        invoiceId: dataInvoice,
      })
    },
    [history, transaction_date, dataInvoice],
  )

  const renderHowToPayButton = useMemo(
    () =>
      enableHowToPayButton && (
        <StyledButton
          label={`${translate('giftShop:howToPay')} (${transaction_method})`}
          onClick={handleHowToPay}
        />
      ),
    [enableHowToPayButton, handleHowToPay, transaction_method, translate],
  )

  const handleRenderPaymentProofMessage = useMemo(
    () =>
      sendProofSent ? (
        <StyledMessageContainer>
          <Icon
            type="circular-check"
            size={16}
            style={{
              alignSelf: 'center',
              marginRight: convertUnit(16),
            }}
            color="success_5"
          />
          <Paragraph
            fontSize="s"
            fontWeight="medium"
            style={{alignSelf: 'center'}}>
            <Paragraph fontSize="s" fontWeight="bold" color="success_5">
              {translate('giftShop:paymentSendProofSuccess')}
              <br />
            </Paragraph>
            <Paragraph fontSize="s" fontWeight="medium" color="gray_5">
              {translate('giftShop:paymentProofAlreadySent')}
            </Paragraph>
          </Paragraph>
        </StyledMessageContainer>
      ) : (
        <StyledMessageContainer>
          <Icon
            type="info"
            size={16}
            style={{
              alignSelf: 'center',
              marginRight: convertUnit(16),
            }}
          />
          <Paragraph
            fontSize="s"
            fontWeight="medium"
            style={{alignSelf: 'center', lineHeight: convertUnit(21)}}>
            {translate('giftShop:paymentWaitOrSendProofFirst')}
            <Paragraph
              fontSize="s"
              fontWeight="medium"
              color="primary_5"
              style={{
                display: 'inline',
                cursor: 'pointer',
              }}
              onClick={() => setSendProofModal((prev) => !prev)}>
              {translate('giftShop:paymentWaitOrSendProofSecond')}
            </Paragraph>
            .
          </Paragraph>
        </StyledMessageContainer>
      ),
    [sendProofSent, setSendProofModal, translate],
  )

  const handleRenderContentModal = useMemo(
    () =>
      highlightedIndex !== undefined && (
        <GiftShopTemplateDetailContentMultipleListModal
          data={{
            content: stateData.data[highlightedIndex].content,
            package: stateData.data[highlightedIndex].package,
            creator_name: stateData.data[highlightedIndex].creator_name,
            price_detail: stateData.data[highlightedIndex].price_detail,
          }}
          visible={highlightedIndex !== undefined}
          toggleModal={() => {
            setHighlightedIndex(undefined)
          }}
          onClickItem={(item) => handleClickPendingItem(item, 'PROCESSED')}
          showDiscount
        />
      ),
    [handleClickPendingItem, highlightedIndex, stateData],
  )

  const handleRenderPaymentProof = useMemo(
    () => (
      <GiftShopTemplatePaymentProofModal
        toggleModal={() => {
          setSendProofModal((prev) => !prev)
        }}
        visible={sendProofModal}
        loading={sendProofLoading}
        onSubmit={handleSubmitPaymentProof}
      />
    ),
    [
      handleSubmitPaymentProof,
      sendProofLoading,
      sendProofModal,
      setSendProofModal,
    ],
  )

  const handleRenderWaitingForPaymentBadge = useMemo(
    () => (
      <StyledBadge>
        <Paragraph fontSize="m" color="primary_5" fontWeight="bold">
          {`${translate('giftShop:status')}: ${translate(
            'giftShop:transactionStatus',
            {
              context: 'PROCESSED',
            },
          )}`}
        </Paragraph>
      </StyledBadge>
    ),
    [translate],
  )

  const handleRenderList = useMemo(() => {
    if (!data || data.length === 0) return <></>

    const entries = data.map((object) => ({
      ...object,
      invoice_status: 'PROCESSED',
      collector_name: '',
    })) as GiftShopInvoiceDetail[]

    const singleContents = data[0].content ?? []
    const singlePackages = data[0].package ?? []

    return creators.length > 1 ? (
      <TransactionDetailContentItemListMultiCreator
        mode={mode}
        data={entries}
        onClickItem={(_item, index) => {
          setHighlightedIndex(index)
        }}
        showDiscount
        topElement={handleRenderWaitingForPaymentBadge}
      />
    ) : (
      <TransactionDetailContentItemListSingleCreator
        mode={mode}
        contents={singleContents}
        packages={singlePackages}
        onClickItem={(item) => {
          handleClickPendingItem(item, 'PROCESSED')
        }}
        showDiscount
        style={{
          marginBottom: mode === 'mobile' ? convertUnit(50) : 0,
        }}
        topElement={handleRenderWaitingForPaymentBadge}
      />
    )
  }, [
    creators.length,
    data,
    handleClickPendingItem,
    handleRenderWaitingForPaymentBadge,
    mode,
  ])

  const handleRenderDetailPurchase = useMemo(
    () => (
      <>
        <StyledInfoItem>
          <Paragraph fontSize="m" fontWeight="medium">
            {translate('giftShop:totalPayment')}
          </Paragraph>
          <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
            {getCurrencyValue(transaction_total)}
          </Paragraph>
          {price_detail.discount.nominal > 0 && (
            <StyledStrikedText fontSize="s" color="gray_3">
              {getCurrencyValue(
                transaction_total + price_detail.discount.nominal,
              )}
            </StyledStrikedText>
          )}
        </StyledInfoItem>
      </>
    ),
    [translate, transaction_total, price_detail.discount.nominal],
  )

  const copyInvoiceToClipboard = useCallback(() => {
    navigator.clipboard.writeText(payment_invoice)
    showSnackbar(translate('global:invoiceCopied'))
  }, [payment_invoice, translate])

  const handleRenderDetailInvoiceID = useMemo(
    () => (
      <>
        <StyledInfoItem>
          <StyledInvoiceIdContainer>
            <Paragraph fontSize="m" fontWeight="medium">
              {translate('giftShop:invoiceID')}
            </Paragraph>
            <StyledCopyIcon
              type="copy"
              onClick={copyInvoiceToClipboard}
              color="primary_5"
              size={12}
            />
          </StyledInvoiceIdContainer>
          <Paragraph
            fontSize="m"
            fontWeight="bold"
            color="primary_5"
            onClick={copyInvoiceToClipboard}>
            {payment_invoice}
          </Paragraph>
        </StyledInfoItem>
      </>
    ),
    [translate, copyInvoiceToClipboard, payment_invoice],
  )

  const renderContent = useMemo(
    () =>
      invoiceIDs && (
        <>
          <StyledContentSection width="50%">
            <StyledDate fontSize="m">
              {(transaction_date &&
                parseDate(transaction_date, 'MMMM D, YYYY')) ||
                ''}
            </StyledDate>
            <TransactionDetailContentHeader
              title={translate('giftShop:transactionStatus_PROCESSED')}
              subTitle={
                creators.length > 1
                  ? `${creators.length} ${translate('global:creators')}`
                  : creators[0]
              }
            />
            {enableHowToPayButton &&
              sendProofSent !== undefined &&
              handleRenderPaymentProofMessage}
            <StyledButtonsContainer>
              {renderHowToPayButton}
            </StyledButtonsContainer>
            <StyledInfoContainer>
              {handleRenderDetailPurchase}
              {handleRenderDetailInvoiceID}
            </StyledInfoContainer>
          </StyledContentSection>
          {handleRenderList}
        </>
      ),
    [
      invoiceIDs,
      transaction_date,
      translate,
      creators,
      enableHowToPayButton,
      sendProofSent,
      handleRenderPaymentProofMessage,
      renderHowToPayButton,
      handleRenderDetailPurchase,
      handleRenderDetailInvoiceID,
      handleRenderList,
    ],
  )

  return (
    <StyledContainer>
      {handleRenderPaymentProof}
      {handleRenderContentModal}
      {renderContent}
    </StyledContainer>
  )
}
